/**
 * Morph Tracing — Vercel AI SDK helper.
 *
 * The Vercel AI SDK emits its own OpenTelemetry spans when you pass
 * `experimental_telemetry`. There is nothing to monkey-patch; instead you tag
 * each call with `metadata()` so Morph can attribute the resulting spans to a
 * user/conversation/event.
 *
 * @example
 * ```ts
 * import { generateText } from "ai";
 * import { metadata } from "@morphllm/morphsdk/tracing/otel";
 *
 * const res = await generateText({
 *   model: openai("gpt-4o"),
 *   prompt: "Hello!",
 *   experimental_telemetry: {
 *     isEnabled: true,
 *     metadata: metadata({ userId: "user-123", convoId: "convo-456" }),
 *   },
 * });
 * ```
 */
import type { MetadataOptions } from './types.js';

/**
 * Reserved metadata keys Morph owns. Custom `properties` can't overwrite these,
 * so attribution (user_id / convo_id / event_id) stays intact.
 *
 * The AI SDK stores metadata as `ai.telemetry.metadata.<key>`; Traceloop's span
 * processor copies them to `traceloop.association.properties.<key>`, which is
 * what ClickHouse views read. Use snake_case names (user_id, convo_id, …).
 */
const RESERVED_KEYS = new Set(['user_id', 'convo_id', 'event_id', 'event_name']);

function uuid(): string {
  // Node 18+ and modern runtimes expose globalThis.crypto.randomUUID.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback: RFC4122-ish without crypto.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Build the metadata object for the Vercel AI SDK's
 * `experimental_telemetry.metadata`. The values are propagated to every span the
 * AI SDK creates for that call. Generate a fresh `eventId` per call for grouping.
 */
export function metadata(opts: MetadataOptions): Record<string, string> {
  const result: Record<string, string> = {
    user_id: opts.userId,
    event_id: opts.eventId ?? uuid(),
  };
  if (opts.convoId) result.convo_id = opts.convoId;
  if (opts.eventName) result.event_name = opts.eventName;
  if (opts.properties) {
    for (const [key, value] of Object.entries(opts.properties)) {
      if (!RESERVED_KEYS.has(key)) result[key] = value;
    }
  }
  return result;
}

export default { metadata };
